import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './system-check.less';
import './webcam.less'
import { Row, Col } from 'antd';
import SystemCheckProgress from './system-check-progress';

let chromeMinimumVersion = process.env.REACT_APP_CHROME_MIN_VERSION ?? '96.0.0' as string;
const [chromeMinimumMajor, chromeMinimumMinor = 0, chromeMinimumPatch = 0] = chromeMinimumVersion?.split('.').map((s: string) => parseInt(s, 10));

let edgeMinimumVersion = process.env.REACT_APP_EDGE_MIN_VERSION ?? '96.0.0' as string;
const [edgeMinimumMajor, edgeMinimumMinor = 0, edgeMinimumPatch = 0] = edgeMinimumVersion?.split('.').map((s: string) => parseInt(s, 10));

function Browser(this: any, props: any) {
    const { browserCheckStatus, setBrowserCheckStatus } = props;
    const [minimumBrowserVersion, setMinimumBrowserVersion] = useState('');
    const [currentBrowserVersion, setCurrentBrowserVersion] = useState(''); 
    const { t } = useTranslation();
    const defaultDelayMs = 2000; // 2 seconds
    const delay = parseInt(process.env.REACT_APP_SYSTEM_CHECK_FAKE_DELAY as string) || defaultDelayMs;
    useEffect(() => {
        setMinimumBrowserVersion(`Chrome ${chromeMinimumMajor}.${chromeMinimumMinor} or Edge ${edgeMinimumMajor}.${edgeMinimumMinor}`);
    },[]);


    useEffect(() => {
        if (browserCheckStatus.inProgress) {
            setTimeout(() => {
                setBrowserCheckStatus((prevStatus: any) => ({
                    ...prevStatus,
                    inProgress: false,
                    completed: true,
                    
                }));
                const browserInfo = isBrowserSupported();
                if(browserInfo.isSupported){
                    setCurrentBrowserVersion(`${browserInfo.browserName}  ${browserInfo.browserVersion}`);
                    setBrowserCheckStatus((prevStatus: any) => ({
                        ...prevStatus,
                        passed: true,
                    }));
                }else{
                    setCurrentBrowserVersion(`${browserInfo.browserName}  ${browserInfo.browserVersion}`);
                    setBrowserCheckStatus((prevStatus: any) => ({
                        ...prevStatus,
                        failed: true,
                    }));
                }
            }, delay);
        }
    }, [browserCheckStatus.inProgress, delay, setBrowserCheckStatus]);

    function isBrowserSupported() {
        const userAgent = navigator.userAgent;
        let browserName: string = "Unknown";
        let browserVersion: string = "0.0"; 
        let browserMajorVersion: number = 0; 

        // --- Detect Chrome ---
        // User-Agent for Chrome typically contains 'Chrome/VERSION' and 'Edg/' for new Edge,
        if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
            browserName = "Chrome";
            // Regex to capture Major and Minor versions
            const match = userAgent.match(/Chrome\/(\d+)\.(\d+)(?:\.\d+)?(?:\.\d+)?/);

            if (match && match[1]) {
                browserMajorVersion = parseInt(match[1], 10);
                let minorVersion = 0;

                if (match[2]) {
                    minorVersion = parseInt(match[2], 10);
                }
                browserVersion = parseFloat(`${browserMajorVersion}.${minorVersion}`).toFixed(1);
                console.log(`Detected Chrome: ${browserMajorVersion}.${minorVersion} (Formatted: ${browserVersion})`);
            } else {
                // Fallback for cases like 'Chrome/XXX' without minor if regex above fails
                const fallbackMatch = userAgent.match(/Chrome\/(\d+)/);
                if (fallbackMatch && fallbackMatch[1]) {
                    browserMajorVersion = parseInt(fallbackMatch[1], 10);
                    browserVersion = parseFloat(`${browserMajorVersion}.0`).toFixed(1);
                }
            }
        }
        // --- Detect Edge ---
        else if (userAgent.includes("Edg")) {
            browserName = "Edge";
            // Regex to capture Major and Minor versions for Edge
            const match = userAgent.match(/Edg\/(\d+)\.(\d+)(?:\.\d+)?(?:\.\d+)?/);

            if (match && match[1]) {
                browserMajorVersion = parseInt(match[1], 10);
                let minorVersion = 0;

                if (match[2]) {
                    minorVersion = parseInt(match[2], 10);
                }
                browserVersion = parseFloat(`${browserMajorVersion}.${minorVersion}`).toFixed(1);
                console.log(`Detected Edge: ${browserMajorVersion}.${minorVersion} (Formatted: ${browserVersion})`);
            } else {
                // Fallback for cases like 'Edg/XXX' without minor
                const fallbackMatch = userAgent.match(/Edg\/(\d+)/);
                if (fallbackMatch && fallbackMatch[1]) {
                    browserMajorVersion = parseInt(fallbackMatch[1], 10);
                    browserVersion = parseFloat(`${browserMajorVersion}.0`).toFixed(1);
                    console.log(`Detected Edge (fallback): ${browserMajorVersion}.0 (Formatted: ${browserVersion})`);
                }
            }
        }

        else if (userAgent.includes("Firefox")) {
            browserName = "Firefox";
            const match = userAgent.match(/Firefox\/(\d+)/);
            if (match && match[1]) {
                browserMajorVersion = parseInt(match[1], 10);
                browserVersion = `${browserMajorVersion}.0`; // Firefox typically just reports major
            }
        }
        else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
            browserName = "Safari";
            // Safari version can be complex, often found after 'Version/'
            const versionMatch = userAgent.match(/Version\/(\d+)\.(\d+)/);
            if (versionMatch && versionMatch[1]) {
                browserMajorVersion = parseInt(versionMatch[1], 10);
                let minorVersion = 0;
                if (versionMatch[2]) {
                    minorVersion = parseInt(versionMatch[2], 10);
                }
                browserVersion = parseFloat(`${browserMajorVersion}.${minorVersion}`).toFixed(1);
            } else {
                // Fallback for Safari, get major from Safari/
                const safariMatch = userAgent.match(/Safari\/(\d+)/);
                if (safariMatch && safariMatch[1]) {
                    browserVersion = "0.0";
                }
            }
        }
        const isSupported = (browserName === "Chrome" && browserMajorVersion >= chromeMinimumMajor) || (browserName === "Edge" && browserMajorVersion >= edgeMinimumMajor);
        
        return {
            browserName: browserName,
            browserVersion: browserVersion, 
            majorVersion: browserMajorVersion,
            isSupported: isSupported
        };
    }

    return (
        <>
            <Row>
                <Col span={1}>
                    <div className={browserCheckStatus.inProgress ? 'system-check-circle-in-progress' : 'system-check-circle'}>1</div>
                    <div className='screen-readers-only'>Step 1 of 6</div>
                </Col>
                <Col span={3} className='system-check-icon with-margin-bottom-24px'>
                <i className="fa-solid fa-window-maximize fa-2xl"></i>
                </Col>
                <Col span={16}>
                    <h3 data-testid="operating-system" className='system-check-name'>{t('SYSTEM_CHECK_BROWSER')}</h3>
                </Col>
                <Col span={4}>
                    <SystemCheckProgress
                        name={t('SYSTEM_CHECK_OPERATING_SYSTEM')}
                        value={currentBrowserVersion}
                        notStarted={browserCheckStatus.notStarted}
                        inProgress={browserCheckStatus.inProgress}
                        passed={browserCheckStatus.passed}
                        failed={browserCheckStatus.failed}
                        warning={browserCheckStatus.warning}
                    />
                </Col>
                <Col span={5} offset={2} className={browserCheckStatus.inProgress || browserCheckStatus.passed || browserCheckStatus.failed || browserCheckStatus.warning ? 'system-check-result-show' : 'system-check-result-hide'}>
                    <div className={browserCheckStatus.passed || browserCheckStatus.failed || browserCheckStatus.warning ? 'system-check-result-show' : 'system-check-result-hide'}>
                        <b>{t('SYSTEM_CHECK_YOUR_SYSTEM')}:</b>
                    </div>
                    <div className={browserCheckStatus.failed || browserCheckStatus.warning ? 'system-check-result-show' : 'system-check-result-hide'}>
                        <div className={browserCheckStatus.warning || browserCheckStatus.failed ? 'system-check-padding-top-5' : ''}><b>{t('SYSTEM_CHECK_MINIMUM')}:</b></div>
                    </div>
                </Col>
                <Col span={14} className={browserCheckStatus.inProgress || browserCheckStatus.passed || browserCheckStatus.failed || browserCheckStatus.warning ? 'system-check-result-show' : 'system-check-result-hide'}>
                    <div className={browserCheckStatus.passed || browserCheckStatus.failed ? 'system-check-result-show' : 'system-check-result-hide'}>
                        {(browserCheckStatus.passed || browserCheckStatus.failed) ? currentBrowserVersion : null}
                    </div>
                    <div className={browserCheckStatus.warning || browserCheckStatus.failed ? 'system-check-result-show' : 'system-check-result-hide'}>
                        <span className={browserCheckStatus.warning ? 'system-check-padding-top-5' : ''}>{minimumBrowserVersion}</span>
                    </div>
                </Col>
                <Col span={3}></Col>
            </Row>
        </>
    )
}

export default Browser;
